import camelcaseKeys from 'camelcase-keys'
import throttle from 'lodash.throttle'
import { create } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import { createWithMutating } from '@/stores'
import { removeItem } from '@/utils'
import { ForbiddenError } from '../errors'
import type { Invitation, InvitationDTO, Member, MemberDTO } from './types'

const THROTTLE_MS = 500

interface MembersStoreActions {
  reset: () => void
  setAccountId: (accountId: number | null) => void
  setMembers: (members: MemberDTO[]) => void
  addMember: (member: Member | MemberDTO) => void
  deleteMember: (id: number) => void
  setInvitations: (invitations: InvitationDTO[]) => void
  addInvitation: (invitation: Invitation | InvitationDTO) => void
  deleteInvitation: (id: number) => void
}

interface MembersStoreMutations {
  inviteMember: (email: string) => Promise<Invitation>
  removeMember: (member: Member) => Promise<void>
  revokeInvitation: (invitation: Invitation) => Promise<void>
}

interface MembersStoreLoaders {
  fetchMembers: () => Promise<void>
  fetchInvitations: () => Promise<void>
}

interface MembersStore {
  accountId: null | number
  members: Member[]
  invitations: Invitation[]
  loadingMembers: boolean
  loadingInvitations: boolean
  mutating: boolean
  actions: MembersStoreActions
  mutations: MembersStoreMutations
  loaders: MembersStoreLoaders
}

export const useMembersStore = create<MembersStore>((set, get, store) => {
  const withMutating = createWithMutating(set)

  return {
    accountId: null,
    members: [],
    invitations: [],
    loadingMembers: true,
    loadingInvitations: true,
    mutating: false,

    actions: {
      reset: () => set(store.getInitialState()),
      setAccountId: (accountId) => set({ accountId }),
      setMembers: (members) =>
        set({ members: camelcaseKeys(members) as Member[] }),
      addMember: (member) =>
        set((state) => ({
          members: [camelcaseKeys(member), ...state.members],
        })),
      deleteMember: (id) =>
        set((state) => ({
          members: removeItem(state.members, id),
        })),
      addInvitation: (invitation) =>
        set((state) => ({
          invitations: [camelcaseKeys(invitation), ...state.invitations],
        })),
      setInvitations: (invitations) =>
        set({ invitations: camelcaseKeys(invitations) as Invitation[] }),
      deleteInvitation: (id) =>
        set((state) => ({
          invitations: removeItem(state.invitations, id),
        })),
    },

    mutations: {
      inviteMember: (email) =>
        withMutating(async () => {
          const accountId = get().accountId
          if (!accountId) {
            throw new Error('Account not found')
          }

          const body = { invitation: { email } }
          const response = await executePost(
            `/accounts/${accountId}/invitations`,
            body,
          )

          const invitation = await response.json()
          get().actions.addInvitation(invitation)
          return camelcaseKeys(invitation)
        }),

      removeMember: (member) =>
        withMutating(async () => {
          const accountId = get().accountId
          if (!accountId) return

          const id = member.id
          get().actions.deleteMember(id)
          try {
            await executeDelete(`/accounts/${accountId}/members/${member.id}`)
          } catch (error) {
            get().actions.addMember(member)
            throw error
          }
        }),

      revokeInvitation: (invitation) =>
        withMutating(async () => {
          const accountId = get().accountId
          if (!accountId) return

          const id = invitation.id
          get().actions.deleteInvitation(id)
          try {
            await executeDelete(`/accounts/${accountId}/invitations/${id}`)
          } catch (error) {
            get().actions.addInvitation(invitation)
            throw error
          }
        }),
    },

    loaders: {
      fetchMembers: throttle(async () => {
        const accountId = get().accountId
        if (!accountId) return

        const response = await executeGet(`/accounts/${accountId}/members`)
        get().actions.setMembers(await response.json())
        set({ loadingMembers: false })
      }, THROTTLE_MS),

      fetchInvitations: throttle(async () => {
        const accountId = get().accountId
        if (!accountId) return

        try {
          const response = await executeGet(
            `/accounts/${accountId}/invitations`,
          )
          get().actions.setInvitations(await response.json())
        } catch (error) {
          const isForbiddenError = error instanceof ForbiddenError
          if (!isForbiddenError) {
            throw error
          }
        }
        set({ loadingInvitations: false })
      }, THROTTLE_MS),
    },
  }
})

export const useMembers = () => useMembersStore((state) => state.members)
export const useInvitations = () =>
  useMembersStore((state) => state.invitations)

export const useLoadingInvitations = () =>
  useMembersStore((state) => state.loadingInvitations)
export const useLoadingMembers = () =>
  useMembersStore((state) => state.loadingMembers)

export const useMembersActions = () => useMembersStore((state) => state.actions)
export const useMembersMutations = () =>
  useMembersStore((state) => state.mutations)
export const useMembersLoaders = () => useMembersStore((state) => state.loaders)
